import {
  Activity,
  Clapperboard,
  HardDrive,
  Images,
  LayoutDashboard,
  LogOut,
  Map,
  MapPin,
  Plane,
  RefreshCw,
  Settings,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, tripPath, type Route, type TripSection } from "../lib/router.tsx";
import { isNative } from "../lib/session.ts";
import { ICON } from "../ui/index.ts";
import { LocationIndicator } from "./LocationIndicator.tsx";
import { PullToRefresh } from "./PullToRefresh.tsx";

type NavItem = { section: TripSection; label: string; short: string; icon: typeof Map };
const ITEM: Record<TripSection, NavItem> = {
  overview: {
    section: "overview",
    label: "Overview",
    short: "Overview",
    icon: LayoutDashboard,
  },
  sync: { section: "sync", label: "Media Sync", short: "Sync", icon: RefreshCw },
  // Reached from Sync ("Manage source permissions"), never listed in the nav.
  sources: { section: "sources", label: "Sync sources", short: "Sources", icon: RefreshCw },
  // Reached from Settings ("Places"), never listed in the nav.
  places: { section: "places", label: "Places", short: "Places", icon: MapPin },
  map: { section: "map", label: "Map & Timeline", short: "Map", icon: Map },
  media: { section: "media", label: "Media Library", short: "Media", icon: Images },
  // The Media Library's other tab, reached by the strip at its top or by swiping — never a
  // nav row of its own, because it is a view of the library rather than a place beside it.
  tags: { section: "tags", label: "Tags", short: "Tags", icon: Images },
  deliverables: {
    section: "deliverables",
    label: "Deliverables",
    short: "Cuts",
    icon: Clapperboard,
  },
  people: { section: "people", label: "People & Sync", short: "People", icon: Users },
  storage: { section: "storage", label: "Storage", short: "Storage", icon: HardDrive },
  activity: { section: "activity", label: "Activity", short: "Activity", icon: Activity },
  settings: { section: "settings", label: "Settings", short: "Settings", icon: Settings },
};

/**
 * What an editor has, and the whole of it. They were invited to cut the trip's footage, not to
 * shoot it: nothing of theirs is uploaded, so there is no Sync and no sharing of their own to
 * report; the capture an Overview summarises and the comings and goings an Activity feed lists are
 * the trip's business rather than the edit's. Two surfaces are left — the map they review from and
 * the deliverables they work on — and the settings that carry signing out.
 */
export const EDITOR_SECTIONS: readonly TripSection[] = ["map", "deliverables", "settings"];

/**
 * What an editor may reach, which is more than their nav lists. `EDITOR_SECTIONS` is the nav (the
 * sidebar and the phone's bar draw one row each from it); this is the guard (`canReach`, which
 * sends an editor anywhere else back to the map). They are two lists because a page can be linked
 * to without being a destination: Settings links to Places, so an editor following that link must
 * not be bounced, but Places is not one of the three things their bar is for. Adding a section to
 * `EDITOR_SECTIONS` to make it reachable would put it in their nav, and leaving it out of this
 * list would make Settings' own link do nothing for them.
 */
export const EDITOR_REACHABLE: readonly TripSection[] = [...EDITOR_SECTIONS, "places"];

/** Whether this reader is an editor and nothing more, which is what narrows the shell to the above. */
export const editorOnly = (roles: string[]) => roles.includes("EDITOR") && !roles.includes("ADMIN");

/** Whether this reader may open `section`: everyone may, but an editor only what `EDITOR_REACHABLE` has. */
export const canReach = (roles: string[], section: TripSection) =>
  !editorOnly(roles) || EDITOR_REACHABLE.includes(section);

/**
 * The desktop sidebar. A different list by role: an editor gets the three above; a traveller the
 * trip, the map and their media; the admin also sees storage. Overview leads there and nowhere
 * else — it is the desktop home and is not in the phone's bar.
 */
export function navFor(roles: string[], native = isNative()): NavItem[] {
  if (editorOnly(roles)) return EDITOR_SECTIONS.map((s) => ITEM[s]);
  const admin = roles.includes("ADMIN");
  const order: TripSection[] = ["overview", "map", "media", "deliverables", "people", "activity"];
  if (admin) order.splice(order.indexOf("activity"), 0, "storage");
  // On the phone, Sync is the traveller's home and comes first.
  if (native) order.unshift("sync");
  order.push("settings");
  return order.map((s) => ITEM[s]);
}

/**
 * The phone's bottom bar. Four destinations at most: the three a traveller touches mid-trip and
 * the settings tab that holds everything else. An editor's bar is their three sections, the same
 * ones the sidebar shows. Labels are one word so none of them ever wraps.
 */
export function tabsFor(roles: string[]): NavItem[] {
  if (editorOnly(roles)) return EDITOR_SECTIONS.map((s) => ITEM[s]);
  return [ITEM.sync, ITEM.map, ITEM.media, ITEM.settings];
}

/** Which tab owns a section, so the bar still reads as current on a page it links out to. */
const TAB_OWNER: Partial<Record<TripSection, TripSection>> = {
  sources: "sync",
  places: "settings",
  tags: "media",
  deliverables: "settings",
  people: "settings",
  storage: "settings",
  activity: "settings",
};

/** Persistent left sidebar and compact header; on phones, a fixed bottom bar. */
export function Shell({
  route,
  tripName,
  roles,
  userName,
  onSignOut,
  children,
}: {
  route: Route;
  tripName: string | null;
  roles: string[];
  userName: string;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const current = (on: boolean) => (on ? ("page" as const) : undefined);
  const inTrip = route.name === "trip";
  const owned = inTrip ? (TAB_OWNER[route.section] ?? route.section) : null;
  return (
    <div className={`shell${inTrip ? " has-tabbar" : ""}`}>
      {/* The one pull the app has: down from the top of a page, to re-read it. */}
      <PullToRefresh />
      <header className="appbar">
        <Link to="/" className="brand">
          <Plane {...ICON} aria-hidden="true" />
          Trip Media
        </Link>
        <span className="spacer" />
        <LocationIndicator />
        <span className="who">{userName}</span>
        <button type="button" className="button small appbar-signout" onClick={onSignOut}>
          <LogOut {...ICON} aria-hidden="true" />
          Sign out
        </button>
      </header>
      <nav className="sidebar" aria-label="Main">
        <div className="nav-group">
          <Link to="/" className="nav-link" aria-current={current(route.name === "trips")}>
            <Plane {...ICON} aria-hidden="true" />
            My Trips
          </Link>
        </div>
        {inTrip && (
          <div className="nav-group" aria-label={tripName ?? "This trip"} role="group">
            <p className="nav-heading">{tripName ?? "This trip"}</p>
            {navFor(roles).map(({ section, label, icon: Icon }) => (
              <Link
                key={section}
                to={tripPath(route.tripId, section)}
                className="nav-link"
                aria-current={current(
                  route.section === section || (section === "sync" && route.section === "sources"),
                )}
              >
                <Icon {...ICON} aria-hidden="true" />
                {label}
              </Link>
            ))}
          </div>
        )}
      </nav>
      <main className="content">
        <div className="page">{children}</div>
      </main>
      {inTrip && (
        <nav className="tabbar" aria-label="Sections">
          {tabsFor(roles).map(({ section, label, short, icon: Icon }) => (
            <Link
              key={section}
              to={tripPath(route.tripId, section)}
              className="tab"
              aria-label={label}
              aria-current={current(owned === section)}
            >
              <span className="tab-mark">
                <Icon {...ICON} aria-hidden="true" />
              </span>
              <span className="tab-label">{short}</span>
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}
